/**
 * The catalog request form, rendered inside the conversation.
 *
 * The assistant used to interrogate the user one field at a time, which is a
 * terrible way to collect eight answers. It now streams a `request_form` event
 * carrying the offering's *real* parameter definitions, and this card puts that
 * form in front of the user to complete and file in one go.
 *
 * Three things are load-bearing:
 *
 *   1. The fields are not written here. `OfferingParameterField` — the same
 *      renderer `/tickets/new` uses — already maps every `OfferingParameterType`
 *      onto the right control, honours `depends_on`, and wires errors to their
 *      inputs. A second copy of that mapping would drift within a release.
 *   2. Validation happens twice on purpose. The client check catches the
 *      obvious things without a round trip; `prepare_service_request` then
 *      re-checks every answer against the offering as Ivanti currently defines
 *      it and returns per-field errors, because the form was captured when the
 *      assistant answered and an option can disappear in between.
 *   3. Filing is a two-step, and the card goes read-only the instant it
 *      succeeds. The submitted state is kept in the query cache rather than in
 *      component state: when a finished turn hands over from the live stream to
 *      the persisted transcript this card unmounts and remounts, and a request
 *      that survived that as an enabled button is a duplicate ticket waiting to
 *      happen.
 */

import { ClipboardList, Clock, Icon, Paperclip, TriangleAlert, X } from "@norican/icons";
import {
  Alert,
  Button,
  Card,
  cn,
  DescriptionList,
  Form,
  Separator,
  Typography,
} from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";
import { useId, useMemo, useRef, useState } from "react";

import {
  ErrorState,
  FieldHighlightLegend,
  FormAnnouncer,
  FormReview,
  REVIEW_HIDES_FORM,
  useFormAnnouncer,
  useFormCheckMessage,
  useSubmitLabel,
} from "@/components/common";
import { FilingAsLine } from "@/components/layout";
import { KioskTicketNumberNote } from "@/features/tickets";
import { newIdempotencyKey } from "@/features/tickets/api/operations";
import {
  answerFor,
  type AnswerMap,
  answerSummary,
  isAnswerMissing,
  isParameterVisible,
  OfferingParameterField,
  seedLookupAnswers,
  seedLookupDisplayNames,
  withCurrentRequirement,
} from "@/features/tickets/components/offering-parameter-fields";
import { useOfferingReviewRows } from "@/features/tickets/components/offering-review";
import { bluestarRolesParameterId } from "@/features/tickets/lib/bluestar-areas";
import { isEmployeeLookup } from "@/features/tickets/lib/offering-lookup";
import { applyPrefill } from "@/features/tickets/lib/offering-prefill";
import { displayText } from "@/features/tickets/lib/translatable";
import { useManagerAutofill } from "@/features/tickets/lib/use-manager-autofill";
import { useLanguage, useT } from "@/i18n";
import {
  checkOfferingAnswer,
  type FormCheckKey,
  needsReviewStep,
  stillNeededCount,
  todayIso,
} from "@/lib/form-checks";
import { logger } from "@/lib/logger";
import { revealFieldSoon, revealFirstProblemSoon } from "@/lib/reveal-first-problem";

import type {
  AssistantRequestFormEvent,
  OfferingParameter,
  ServiceRequestFieldError,
  ServiceRequestSubmission,
  TicketOfferingAnswer,
} from "./contract";
import { assistantKeys, usePrepareServiceRequest, useSubmitServiceRequest } from "./hooks";
import { useInlineForm } from "./inline-forms";
import { type ChatFile, unattachedFileNames } from "./lib/chat-files";
import { TicketRefCard } from "./ticket-ref-card";

/**
 * The per-field failures the contract names.
 *
 * The server sends a `message` alongside each one, but it is written for the
 * model — "Ask the user for it" — so only the `code` is shown, translated. An
 * unrecognised code falls back to a generic message rather than being dropped:
 * a field the user cannot see is wrong is a field they cannot fix.
 */
const FIELD_ERROR_CODES = [
  "REQUIRED_MISSING",
  "INVALID_OPTION",
  "INVALID_NUMBER",
  "INVALID_DATE",
  "INVALID_DATETIME",
  "INVALID_BOOLEAN",
  "TOO_LONG",
  "ATTACHMENT_REQUIRED",
  "UNKNOWN_PARAMETER",
] as const;

type FieldErrorCode = (typeof FIELD_ERROR_CODES)[number];

/** Errors indexed by the parameter they belong to. */
type FieldErrorMap = Record<string, FieldErrorCode>;

/** Plain checks (7 Oct) indexed by the parameter they belong to. */
type FieldCheckMap = Record<string, FormCheckKey>;

/** The plain checks for answers that are present (a missing one is already an error). */
function checkAnswers(
  parameters: OfferingParameter[],
  answers: AnswerMap,
  errors: FieldErrorMap,
  unpicked: Record<string, boolean>,
): FieldCheckMap {
  const today = todayIso();
  const checks: FieldCheckMap = {};
  for (const parameter of parameters) {
    if (errors[parameter.parameter_id] !== undefined) {
      continue;
    }
    const problem = checkOfferingAnswer(parameter, answerFor(answers, parameter.parameter_id), {
      today,
      unpicked: unpicked[parameter.parameter_id] === true,
    });
    if (problem !== null) {
      checks[parameter.parameter_id] = problem;
    }
  }
  return checks;
}

function asFieldErrorCode(code: string | null | undefined): FieldErrorCode {
  return (FIELD_ERROR_CODES as readonly string[]).includes(code ?? "")
    ? (code as FieldErrorCode)
    : "INVALID_OPTION";
}

/** The parameters that are actually in play, in the order the offering declares. */
function visibleParameters(
  parameters: OfferingParameter[],
  answers: AnswerMap,
): OfferingParameter[] {
  return [...parameters]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .filter((parameter) => isParameterVisible(parameter, answers))
    .map((parameter) => withCurrentRequirement(parameter, answers));
}

/**
 * Everything that can be checked without asking the server.
 *
 * Required fields, option membership, the arity a `SELECT` allows and the
 * length the offering declares. Anything subtler — an option withdrawn since
 * the form was streamed, a cross-field rule — is left to
 * `prepare_service_request`, which is the only thing that knows.
 */
function validateAnswers(parameters: OfferingParameter[], answers: AnswerMap): FieldErrorMap {
  const errors: FieldErrorMap = {};

  for (const parameter of parameters) {
    const answer = answerFor(answers, parameter.parameter_id);

    if (parameter.type === "ATTACHMENT") {
      // A required attachment is only missing when nothing was uploaded; a file
      // that is still uploading has no id yet, so Submit is held elsewhere.
      if (parameter.required === true && (answer.attachmentIds ?? []).length === 0) {
        errors[parameter.parameter_id] = "ATTACHMENT_REQUIRED";
      }
      continue;
    }

    if (isAnswerMissing(parameter, answer)) {
      errors[parameter.parameter_id] = "REQUIRED_MISSING";
      continue;
    }

    if (parameter.type === "SELECT" || parameter.type === "MULTISELECT") {
      // A directory lookup is declared a `SELECT` but is not answered from
      // `options`: those are the truncated, GUID-labelled remnant the employee
      // picker exists to replace, and checking a picked RecId against them
      // would reject every colleague past the first couple of hundred — the
      // exact bug this field was changed to fix. Safe here because the only
      // way to answer this control is to pick a row the directory returned.
      // Note the assistant path has no equivalent guarantee: the backend skips
      // option-membership whenever `options` is empty and does not yet check
      // the id against the directory, so a model-invented RecId would pass.
      const known = isEmployeeLookup(parameter)
        ? new Set<string>()
        : new Set((parameter.options ?? []).map((option) => option.value_id));
      const chosen = answer.valueIds;
      if (known.size > 0 && chosen.some((valueId) => !known.has(valueId))) {
        errors[parameter.parameter_id] = "INVALID_OPTION";
      } else if (parameter.type === "SELECT" && chosen.length > 1) {
        errors[parameter.parameter_id] = "INVALID_OPTION";
      }
      continue;
    }

    const value = answer.value ?? "";
    if (parameter.type === "NUMBER" && value !== "" && Number.isNaN(Number(value))) {
      errors[parameter.parameter_id] = "INVALID_NUMBER";
      continue;
    }
    if (parameter.max_length != null && value.length > parameter.max_length) {
      errors[parameter.parameter_id] = "TOO_LONG";
    }
  }

  return errors;
}

export interface RequestFormCardProps {
  /** The streamed form definition. */
  form: AssistantRequestFormEvent;
  /**
   * Identity of this card across a remount.
   *
   * Scopes the "already filed" memo in the query cache, so it must be stable
   * for the life of the conversation — the assistant message id plus the
   * offering, not an array index that shifts.
   */
  formKey: string;
  /** Threaded onto the draft so the request lands against the right conversation. */
  conversationId?: string | null;
  /**
   * Files attached in the chat up to this turn. They go with the request —
   * the person can take any of them off — exactly as the incident card
   * carries the turn's files (LIVE #10578, 8 Oct).
   */
  chatFiles?: ChatFile[];
  className?: string;
}

export function RequestFormCard({
  form,
  formKey,
  conversationId = null,
  chatFiles = [],
  className,
}: RequestFormCardProps) {
  const { t } = useT("assistant");
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const headingId = useId();

  const prepare = usePrepareServiceRequest();
  const submitRequest = useSubmitServiceRequest();

  // What the person already told the assistant is written over the blank
  // form once, at mount. Every field stays editable; the hint under a
  // prefilled field goes away as soon as they change it.
  const [initial] = useState(() => {
    const applied = applyPrefill(form.parameters, seedLookupAnswers(form.parameters), form.prefill);
    // Counts only: the values are the person's own words.
    logger.debug(
      "[assistant] request form prefilled",
      form.offering_id,
      applied.prefilledIds.length,
    );
    return applied;
  });
  const [answers, setAnswers] = useState<AnswerMap>(initial.answers);
  const [prefilledIds, setPrefilledIds] = useState<ReadonlySet<string>>(
    () => new Set(initial.prefilledIds),
  );
  const [employeeNames, setEmployeeNames] = useState<Record<string, string>>(() =>
    seedLookupDisplayNames(form.parameters),
  );
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [fieldChecks, setFieldChecks] = useState<FieldCheckMap>({});
  /** Person lookups with a name typed but nobody picked from the list. */
  const [unpicked, setUnpicked] = useState<Record<string, boolean>>({});
  /** "Here's what IT will get" is on screen instead of the fields (7 Oct). */
  const [reviewing, setReviewing] = useState(false);
  const { message: announcement, announce } = useFormAnnouncer();
  const submitLabel = useSubmitLabel();
  const checkMessage = useFormCheckMessage();
  const reviewRows = useOfferingReviewRows();
  const { t: tCommon } = useT("common");
  const clearFieldError = (parameterId: string) => {
    setFieldErrors((previous) => {
      if (previous[parameterId] === undefined) {
        return previous;
      }
      const { [parameterId]: _cleared, ...rest } = previous;
      return rest;
    });
    setFieldChecks((previous) => {
      if (previous[parameterId] === undefined) {
        return previous;
      }
      const { [parameterId]: _cleared, ...rest } = previous;
      return rest;
    });
  };
  const [rejected, setRejected] = useState(false);
  /* Chat files the person took off this request with ×. */
  const [removedChatIds, setRemovedChatIds] = useState<ReadonlySet<string>>(() => new Set());
  const carriedFiles = chatFiles.filter((file) => !removedChatIds.has(file.attachmentId));
  /* Server errors against parameters the form is not showing (hidden by a
     `depends_on`, or unknown to the streamed form). Nothing is highlighted for
     them, so they are named in the summary instead. */
  const [hiddenErrorNames, setHiddenErrorNames] = useState<string[]>([]);
  /* Prepare answered with neither a draft nor a field error to point at. */
  const [prepareIncomplete, setPrepareIncomplete] = useState(false);
  /* One Idempotency-Key per attempt, keyed on what the person answered: a
     retry of the same answers (a double click, a timeout pressed through)
     reuses it and the server replays the first filing instead of raising a
     twin — even though each prepare mints a new draft id. Editing an answer
     is a new attempt and gets a new key. */
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);
  /* Set synchronously on the first tap: `isPending` is React state and only
     flips after a re-render, too late to stop a fast double-tap filing twice. */
  const inFlight = useRef(false);
  /* On a phone the submit button is below the fold of the card: a blocked or
     failed submit brings the problem into view instead of failing silently. */
  const formRef = useRef<HTMLFormElement>(null);
  const revealProblem = () => revealFirstProblemSoon(() => formRef.current);
  const [submission, setSubmission] = useState<ServiceRequestSubmission | null>(
    () =>
      queryClient.getQueryData<ServiceRequestSubmission>(assistantKeys.serviceRequest(formKey)) ??
      null,
  );

  const visible = useMemo(() => visibleParameters(form.parameters, answers), [form, answers]);
  // 8 Oct: with the system Bluestar, "Roles or access needed" is its area picker.
  const bluestarRolesId = bluestarRolesParameterId(form.parameters, answers);

  // "User manager should be auto selected based on the user selected." The
  // hook owns the never-clobber rule; the card only tells it who was picked.
  const managerAutofill = useManagerAutofill(form.parameters, setAnswers);

  const submitted = submission !== null;
  const busy = prepare.isPending || submitRequest.isPending;
  // 14 Oct: Cancel closes the form without sending; the chat's composer comes back.
  const [cancelled, setCancelledState] = useState(
    () => queryClient.getQueryData<boolean>(assistantKeys.formCancelled(formKey)) === true,
  );
  const setCancelled = (value: boolean) => {
    queryClient.setQueryData(assistantKeys.formCancelled(formKey), value);
    setCancelledState(value);
  };
  useInlineForm(formKey, !submitted && !cancelled);
  const hasFieldErrors = Object.keys(fieldErrors).length + Object.keys(fieldChecks).length > 0;
  // The count on the button (7 Oct): the questions still tinted "still needed".
  const stillNeeded = stillNeededCount(
    visible.map((parameter) =>
      isAnswerMissing(parameter, answerFor(answers, parameter.parameter_id)),
    ),
  );
  const reviewFirst = needsReviewStep({
    form: "service-request",
    askedQuestions: visible.length,
    collect: false,
  });

  /** Check everything; mark, reveal and announce what is wrong. Returns whether Submit is held. */
  const holdSubmit = (): boolean => {
    const localErrors = validateAnswers(visible, answers);
    const localChecks = checkAnswers(visible, answers, localErrors, unpicked);
    const errorCount = Object.keys(localErrors).length;
    const checkCount = Object.keys(localChecks).length;
    setFieldErrors(localErrors);
    setFieldChecks(localChecks);
    setHiddenErrorNames([]);
    setPrepareIncomplete(false);
    setRejected(errorCount + checkCount > 0);
    if (errorCount + checkCount === 0) {
      return false;
    }
    revealProblem();
    const needed = Object.values(localErrors).filter(
      (code) => code === "REQUIRED_MISSING" || code === "ATTACHMENT_REQUIRED",
    ).length;
    announce(needed, errorCount - needed + checkCount);
    return true;
  };

  const backToForm = (parameterId?: string) => {
    setReviewing(false);
    if (parameterId !== undefined) {
      revealFieldSoon(() => formRef.current, parameterId);
    }
  };

  const handleSubmit = () => {
    if (submitted || busy || inFlight.current) {
      return;
    }

    if (holdSubmit()) {
      return;
    }
    // Longer forms show "Here's what IT will get" first (7 Oct).
    if (reviewFirst && !reviewing) {
      setReviewing(true);
      return;
    }

    const payloadAnswers: TicketOfferingAnswer[] = visible.map((parameter) => {
      const answer = answerFor(answers, parameter.parameter_id);
      // An ATTACHMENT parameter's answer is the files the user uploaded to the
      // draft; they travel on `attachment_ids`, not `value`/`value_ids`. The
      // ids are the user's own uploads — nothing the model supplied.
      if (parameter.type === "ATTACHMENT") {
        return {
          parameter_id: parameter.parameter_id,
          attachment_ids: answer.attachmentIds ?? [],
        };
      }
      return {
        parameter_id: parameter.parameter_id,
        value: answer.value,
        value_ids: answer.valueIds,
        attachment_ids: [],
      };
    });

    logger.debug("[assistant] preparing inline request", form.offering_id);

    const chatAttachmentIds = carriedFiles.map((file) => file.attachmentId);
    const fingerprint = JSON.stringify([
      form.offering_id,
      conversationId,
      payloadAnswers,
      chatAttachmentIds,
    ]);
    if (attempt.current === null || attempt.current.fingerprint !== fingerprint) {
      attempt.current = { fingerprint, key: newIdempotencyKey() };
    }
    const idempotencyKey = attempt.current.key;

    inFlight.current = true;
    void (async () => {
      try {
        const draft = await prepare.mutateAsync({
          conversation_id: conversationId,
          offering_id: form.offering_id,
          // The offering's own name and blurb are the only description the
          // browser has; the specifics live in the answers below it.
          subject: form.offering_name.slice(0, 255),
          description: (form.description ?? form.offering_name).slice(0, 4000),
          body_lang: language,
          answers: payloadAnswers,
          chat_attachment_ids: chatAttachmentIds,
        });

        const serverErrors: ServiceRequestFieldError[] = draft.field_errors ?? [];
        if (serverErrors.length > 0 || draft.draft_id == null || draft.draft_id === "") {
          logger.warn("[assistant] inline request rejected", form.offering_id, serverErrors);
          setFieldErrors(
            Object.fromEntries(
              serverErrors.map((error) => [error.parameter_id, asFieldErrorCode(error.code)]),
            ),
          );
          const shownIds = new Set(visible.map((parameter) => parameter.parameter_id));
          setHiddenErrorNames(
            serverErrors
              .filter((error) => !shownIds.has(error.parameter_id))
              .map((error) => {
                const known = form.parameters.find(
                  (parameter) => parameter.parameter_id === error.parameter_id,
                );
                return known === undefined
                  ? (error.name ?? error.parameter_id)
                  : displayText(known.name);
              }),
          );
          // No draft and nothing to point at: say it failed rather than stop
          // silently with the button re-enabled.
          setPrepareIncomplete(serverErrors.length === 0);
          setRejected(serverErrors.length > 0);
          setReviewing(false);
          revealProblem();
          return;
        }

        const result = await submitRequest.mutateAsync({
          formKey,
          body: {
            draft_id: draft.draft_id,
            user_confirmed: true,
            conversation_id: conversationId,
          },
          idempotencyKey,
        });
        logger.debug("[assistant] inline request filed", result.ticket.ticket_number);
        setFieldErrors({});
        setRejected(false);
        setSubmission(result);
      } catch (error) {
        // Rendered by the inline ErrorState below; rethrowing would surface as
        // an unhandled rejection in the console and tell the user nothing.
        logger.warn("[assistant] could not file the inline request", error);
        setReviewing(false);
        revealProblem();
      } finally {
        inFlight.current = false;
      }
    })();
  };

  return (
    <section aria-labelledby={headingId} className={cn("w-full", className)}>
      <Card variant="outline" accent="left" accentTone={submitted ? "success" : "accent"}>
        <Card.Header>
          <div className="flex min-w-0 items-start gap-3">
            <Icon icon={ClipboardList} size="md" tone={submitted ? "success" : "accent"} />
            <div className="flex min-w-0 flex-col gap-1">
              <Typography as="h3" variant="heading-5" id={headingId}>
                {form.offering_name}
              </Typography>
              <Typography variant="body-sm" tone="muted">
                {submitted ? t("requestForm.submittedTitle") : t("requestForm.title")}
              </Typography>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          <div className="flex flex-col gap-4">
            {form.description === null || form.description === "" ? null : (
              <Typography variant="body-sm" tone="muted">
                {form.description}
              </Typography>
            )}

            {form.requires_approval ? (
              <Alert tone="info" icon={<Icon icon={Clock} size="sm" tone="info" />}>
                {t("requestForm.approvalNotice")}
              </Alert>
            ) : null}

            {form.delivery_days === null ? null : (
              <Typography variant="caption" tone="muted">
                {t("requestForm.delivery", { count: form.delivery_days })}
              </Typography>
            )}

            <Separator />

            {submitted ? (
              <SubmittedSummary
                parameters={form.parameters}
                answers={answers}
                employeeNames={employeeNames}
                submission={submission}
                chatFiles={carriedFiles}
              />
            ) : cancelled ? (
              <CancelledNote onReopen={() => setCancelled(false)} />
            ) : (
              <Form
                ref={formRef}
                // The summary hides the fields rather than unmounting them
                // (uploads would be lost). Only `data-review-keep` stays.
                className={cn("flex flex-col gap-6", reviewing && REVIEW_HIDES_FORM)}
                onFormSubmit={handleSubmit}
              >
                {/* Assertive by tone: the user pressed Submit and nothing
                    happened, which needs saying, not whispering. */}
                {rejected && hasFieldErrors ? (
                  <Alert
                    tone="danger"
                    icon={<Icon icon={TriangleAlert} size="sm" tone="danger" />}
                    data-form-problem
                  >
                    <div className="flex flex-col gap-1">
                      <span>
                        {t("requestForm.errorSummary", {
                          count: Object.keys(fieldErrors).length + Object.keys(fieldChecks).length,
                        })}
                      </span>
                      {hiddenErrorNames.length === 0 ? null : (
                        <span>
                          {t("requestForm.hiddenFieldErrors", {
                            fields: hiddenErrorNames.join(", "),
                          })}
                        </span>
                      )}
                    </div>
                  </Alert>
                ) : null}

                {reviewing ? (
                  <FormReview
                    rows={[
                      ...reviewRows(visible, answers, employeeNames, backToForm),
                      ...(carriedFiles.length === 0
                        ? []
                        : [
                            {
                              key: "__chat_files",
                              label: t("requestForm.chatFilesReview"),
                              value: carriedFiles.map((file) => file.name).join(", "),
                            },
                          ]),
                    ]}
                    onBack={() => backToForm()}
                    onSend={handleSubmit}
                    sending={busy}
                    sendLabel={t("requestForm.submit")}
                    footer={<FilingAsLine />}
                  />
                ) : null}

                {visible.length > 0 ? <FieldHighlightLegend /> : null}

                {groupIntoSections(visible).map((section) => (
                  <div key={section.key} className="flex flex-col gap-4">
                    {section.heading === null ? null : (
                      <Typography variant="heading-6" className="text-fg-muted">
                        {section.heading}
                      </Typography>
                    )}
                    {section.parameters.map((parameter) => (
                      <OfferingParameterField
                        key={parameter.parameter_id}
                        parameter={parameter}
                        answer={answerFor(answers, parameter.parameter_id)}
                        checkOnLeave
                        error={
                          fieldErrors[parameter.parameter_id] === undefined
                            ? checkMessage(fieldChecks[parameter.parameter_id])
                            : t(`requestForm.fieldError.${fieldErrors[parameter.parameter_id]}`)
                        }
                        onTextPendingChange={(pending) =>
                          setUnpicked((previous) =>
                            previous[parameter.parameter_id] === pending
                              ? previous
                              : { ...previous, [parameter.parameter_id]: pending },
                          )
                        }
                        autoFilled={managerAutofill.isAutoFilled(parameter.parameter_id)}
                        prefilled={prefilledIds.has(parameter.parameter_id)}
                        bluestarAreas={parameter.parameter_id === bluestarRolesId}
                        onEmployeeSelect={(employee) => {
                          clearFieldError(parameter.parameter_id);
                          setEmployeeNames((previous) => {
                            if (employee === null) {
                              const { [parameter.parameter_id]: _cleared, ...rest } = previous;
                              return rest;
                            }
                            return {
                              ...previous,
                              [parameter.parameter_id]: employee.display_name,
                            };
                          });
                          managerAutofill.fillFrom(parameter.parameter_id, employee, answers);
                        }}
                        onChange={(next) => {
                          // A field's error clears as soon as it changes: the
                          // form will not submit while one is marked invalid.
                          clearFieldError(parameter.parameter_id);
                          // Told first, so a value the user typed over stops
                          // being the hook's to overwrite from here on.
                          managerAutofill.noteEdit(parameter.parameter_id, next);
                          if (prefilledIds.has(parameter.parameter_id)) {
                            setPrefilledIds((previous) => {
                              const rest = new Set(previous);
                              rest.delete(parameter.parameter_id);
                              return rest;
                            });
                          }
                          setAnswers((previous) => ({
                            ...previous,
                            [parameter.parameter_id]: next,
                          }));
                        }}
                      />
                    ))}
                  </div>
                ))}

                {prepare.isError ? (
                  <div data-form-problem>
                    <ErrorState variant="inline" error={prepare.error} />
                  </div>
                ) : null}
                {/* No error object: the generic "Something went wrong" copy. */}
                {prepareIncomplete && !prepare.isError ? (
                  <div data-form-problem>
                    <ErrorState variant="inline" />
                  </div>
                ) : null}
                {submitRequest.isError ? (
                  <div data-form-problem>
                    <ErrorState variant="inline" error={submitRequest.error} offerHelpdeskContact />
                  </div>
                ) : null}

                {carriedFiles.length === 0 ? null : (
                  <ChatFileList
                    files={carriedFiles}
                    disabled={busy}
                    onRemove={(attachmentId) =>
                      setRemovedChatIds((previous) => new Set([...previous, attachmentId]))
                    }
                  />
                )}

                {/* Shared PCs: whose name this goes out under. */}
                <FilingAsLine className="-mb-3" />

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="submit"
                    loading={busy}
                    disabled={busy}
                    onClick={(event) => {
                      // Checked before the Form's own validation can swallow
                      // the press without a word (7 Oct).
                      if (!submitted && !busy && holdSubmit()) {
                        event.preventDefault();
                      }
                    }}
                  >
                    {submitLabel(
                      reviewFirst ? tCommon("formCheck.review.next") : t("requestForm.submit"),
                      stillNeeded,
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    tone="neutral"
                    disabled={busy}
                    onClick={() => setCancelled(true)}
                  >
                    {t("inlineForm.cancel")}
                  </Button>
                  <Typography variant="caption" tone="muted">
                    {t("requestForm.submitHint")}
                  </Typography>
                </div>
                <FormAnnouncer message={announcement} />
              </Form>
            )}
          </div>
        </Card.Body>
      </Card>
    </section>
  );
}

/** A cancelled chat form (14 Oct): nothing was sent; it can be opened again. */
export function CancelledNote({ onReopen }: { onReopen: () => void }) {
  const { t } = useT("assistant");
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Typography variant="body-sm" tone="muted">
        {t("inlineForm.cancelled")}
      </Typography>
      <Button type="button" variant="outline" tone="neutral" size="sm" onClick={onReopen}>
        {t("inlineForm.reopen")}
      </Button>
    </div>
  );
}

interface ChatFileListProps {
  files: ChatFile[];
  disabled: boolean;
  onRemove: (attachmentId: string) => void;
}

/** "Attached from the chat: …" — what goes to IT with the request, each removable. */
function ChatFileList({ files, disabled, onRemove }: ChatFileListProps) {
  const { t } = useT("assistant");
  const { t: tCommon } = useT();
  return (
    <section aria-label={t("requestForm.chatFilesLabel")} className="flex flex-col gap-2">
      <Typography variant="caption" tone="muted">
        {t("requestForm.chatFilesLabel")}
      </Typography>
      <ul className="flex flex-wrap gap-2">
        {files.map((file) => (
          <li
            key={file.attachmentId}
            className="bg-bg-subtle border-border-subtle flex max-w-full items-center gap-1.5 rounded-md border py-1 pr-1 pl-2"
          >
            <span className="text-fg-muted flex">
              <Icon icon={Paperclip} size="xs" />
            </span>
            <Typography variant="body-sm" className="min-w-0 truncate">
              {file.name}
            </Typography>
            <Button
              type="button"
              variant="ghost"
              tone="neutral"
              size="sm"
              iconOnly
              disabled={disabled}
              aria-label={tCommon("attachment.remove", { name: file.name })}
              className="h-6 min-h-0 w-6 p-0"
              onClick={() => onRemove(file.attachmentId)}
            >
              <Icon icon={X} size="xs" />
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface SubmittedSummaryProps {
  parameters: OfferingParameter[];
  answers: AnswerMap;
  /** Parameter id → the picked colleague's name, for the directory lookups. */
  employeeNames: Record<string, string>;
  submission: ServiceRequestSubmission;
  /** The chat files sent with it; empty after a remount (the warning still names them). */
  chatFiles: ChatFile[];
}

/**
 * The card after it has been filed.
 *
 * The inputs are replaced rather than disabled: `OfferingParameterField` has no
 * `disabled` passthrough, and a greyed-out form that still looks fillable is a
 * worse answer than a plain record of what was sent.
 */
function SubmittedSummary({
  parameters,
  answers,
  employeeNames,
  submission,
  chatFiles,
}: SubmittedSummaryProps) {
  const { t } = useT("assistant");
  const unattached = submission.unattached_answers ?? [];
  const missingFiles = submission.unattached_files ?? [];
  const missingFileNames = unattachedFileNames(
    missingFiles,
    chatFiles,
    t("requestForm.unknownFile"),
  );
  const missingIds = new Set(missingFiles.map((file) => file.attachment_id));
  const sentChatFiles = chatFiles
    .filter((file) => !missingIds.has(file.attachmentId))
    .map((file) => file.name);

  const items = visibleParameters(parameters, answers)
    .map((parameter) => ({
      label: displayText(parameter.name),
      value: answerSummary(parameter, answerFor(answers, parameter.parameter_id), employeeNames),
    }))
    .concat(
      sentChatFiles.length === 0
        ? []
        : [{ label: t("requestForm.chatFilesReview"), value: sentChatFiles.join(", ") }],
    )
    .filter((item) => item.value !== "")
    .map((item) => ({
      label: item.label,
      value: <span className="break-words">{item.value}</span>,
    }));

  return (
    <div className="flex flex-col gap-4">
      <Alert tone="success" title={t("requestForm.submittedTitle")}>
        {/*
         * The number is interpolated, not decorative: without it this read
         * "Reference {{number}}." in the UI. `ticket_number` is optional on
         * the wire, so fall back to a sentence that does not promise one.
         */}
        {submission.ticket.ticket_number
          ? t("requestForm.submittedBody", { number: submission.ticket.ticket_number })
          : t("requestForm.submittedBodyNoNumber")}
      </Alert>

      <KioskTicketNumberNote number={submission.ticket.ticket_number} />

      {/*
       * Answers are written as separate records after the request exists, so a
       * request can be filed holding only some of what was typed. Saying so is
       * the whole point: an unqualified "sent" over a half-empty ticket is how
       * every request went out empty without anyone noticing.
       */}
      {unattached.length === 0 ? null : (
        <Alert tone="warning" title={t("requestForm.unattachedTitle")}>
          {t("requestForm.unattachedBody", { fields: unattached.join(", ") })}
        </Alert>
      )}

      {/* A file that did not make it is said out loud, never dropped: the
          fulfiller works from what is on the record (LIVE #10578). */}
      {missingFileNames.length === 0 ? null : (
        <Alert tone="warning" title={t("requestForm.unattachedFilesTitle")}>
          {t("requestForm.unattachedFilesBody", { files: missingFileNames.join(", ") })}
        </Alert>
      )}

      <TicketRefCard ticket={submission.ticket} />

      {items.length === 0 ? null : (
        <section aria-label={t("requestForm.summaryTitle")} className="flex flex-col gap-2">
          <Typography as="h4" variant="label">
            {t("requestForm.summaryTitle")}
          </Typography>
          <DescriptionList items={items} orientation="horizontal" />
        </section>
      )}
    </div>
  );
}

export interface RequestFormListProps {
  forms: AssistantRequestFormEvent[];
  /** Prefix for each card's cache key — the assistant message id. */
  scopeId: string;
  conversationId?: string | null;
  /** Files attached in the chat up to this turn; see `RequestFormCardProps.chatFiles`. */
  chatFiles?: ChatFile[];
  className?: string;
}

/** Every form the assistant offered in one turn. Renders nothing when empty. */
/**
 * Group the visible fields under Ivanti's own section headings.
 *
 * The CRM access form is six sections - "Who is this for?", "Request details",
 * "Base data", "Security", "Notes", "Approval" - and twenty-one fields in one
 * undifferentiated list is materially harder to complete than the same fields
 * under their headings. The grouping is the form designer's, carried through
 * from the offering definition rather than invented here.
 *
 * Order comes from `category_order` (the section's position on the real form)
 * then `sort_order` within it, so the result reads in the order it was designed
 * in rather than alphabetically or by whatever order the API happened to
 * return.
 *
 * Offerings that declare no sections come back as a single unheaded group, so
 * a simple two-field form does not grow a spurious heading.
 */
function groupIntoSections(
  parameters: OfferingParameter[],
): { key: string; heading: string | null; parameters: OfferingParameter[] }[] {
  const sections = new Map<
    string,
    { key: string; heading: string | null; order: number; parameters: OfferingParameter[] }
  >();

  for (const parameter of parameters) {
    const heading = parameter.category?.text?.trim() ?? "";
    const key = heading === "" ? "__ungrouped" : heading;
    const existing = sections.get(key);
    if (existing === undefined) {
      sections.set(key, {
        key,
        heading: heading === "" ? null : heading,
        order: parameter.category_order ?? 0,
        parameters: [parameter],
      });
    } else {
      existing.parameters.push(parameter);
    }
  }

  return [...sections.values()]
    .sort((a, b) => a.order - b.order)
    .map((section) => ({
      key: section.key,
      heading: section.heading,
      parameters: [...section.parameters].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    }));
}

export function RequestFormList({
  forms,
  scopeId,
  conversationId = null,
  chatFiles = [],
  className,
}: RequestFormListProps) {
  if (forms.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {forms.map((form) => (
        <RequestFormCard
          key={form.offering_id}
          form={form}
          formKey={`${scopeId}:${form.offering_id}`}
          conversationId={conversationId}
          chatFiles={chatFiles}
        />
      ))}
    </div>
  );
}
